import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { hasOpenDialogs } from '../../../../core/functions/dialog-helper';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { SubmissionEmailVerificationRequiredDialogMessage } from '../../../application/shared/constants/submission-dialog-message';
import { UserEmailVerificationService } from '../../../user/shared/services/user-email-verification/user-email-verification.service';

@Injectable({
    providedIn: 'root',
})
export class ApplicationUserEmailVerificationService {
    private readonly alertService = inject(AlertService);
    private readonly userEmailVerificationService = inject(
        UserEmailVerificationService,
    );
    private readonly dialog = inject(MatDialog);

    checkEmailVerification(userId: number | null): Observable<boolean> {
        if (hasOpenDialogs(this.dialog)) {
            return of(false);
        }

        if (userId === null) {
            this.alertService.displayErrorAlert(
                'Unable to verify email. User ID is null.',
            );
            return of(false);
        }

        return this.userEmailVerificationService
            .getEmailVerificationStatus(userId)
            .pipe(
                switchMap((status) => {
                    if (status.isEmailVerified) {
                        return of(true);
                    }

                    const dialogRef = this.dialog.open(ModalComponent, {
                        disableClose: true,
                        data: {
                            title: SubmissionEmailVerificationRequiredDialogMessage.modalTitle,
                            content:
                                SubmissionEmailVerificationRequiredDialogMessage.content.replace(
                                    '{emailAddress}',
                                    status.userEmailAddress,
                                ),
                            primaryAction: {
                                name: SubmissionEmailVerificationRequiredDialogMessage
                                    .secondaryAction.name,
                                title: SubmissionEmailVerificationRequiredDialogMessage
                                    .secondaryAction.title,
                            },
                            secondaryAction: {
                                name: SubmissionEmailVerificationRequiredDialogMessage
                                    .primaryAction.name,
                                title: SubmissionEmailVerificationRequiredDialogMessage
                                    .primaryAction.title,
                                action: () => {
                                    this.userEmailVerificationService.sendVerificationEmail(
                                        userId,
                                    );
                                },
                            },
                            isShowCancel: true,
                        },
                    });

                    return dialogRef.afterClosed();
                }),
            );
    }
}
